<!-- src/app/pages/may-day-signup/may-day-signup.component.html -->
<section class="may-day-page">

  <!-- HERO -->
  <div class="hero">
    <div class="hero__badge">May 1, 2026 · May Day</div>
    <h1>Voter Registration Drive</h1>
    <p class="hero__sub">
      Join Lydia Christensen and volunteers across Michigan to help register voters.
      Every registered voter is a voice for change.
    </p>
  </div>

  <div class="page-body">

    <!-- EVENT DETAILS -->
    <div class="details-grid">
      <div class="detail-card">
        <span class="detail-card__icon">📅</span>
        <h3>When</h3>
        <p>Thursday, May 1, 2026<br>International Workers' Day</p>
      </div>
      <div class="detail-card">
        <span class="detail-card__icon">📍</span>
        <h3>Where</h3>
        <p>Statewide — Michigan<br>Your community, your precinct</p>
      </div>
      <div class="detail-card">
        <span class="detail-card__icon">🗳️</span>
        <h3>What We're Doing</h3>
        <p>Registering voters and knocking doors for Lydia's Senate campaign</p>
      </div>
      <div class="detail-card">
        <span class="detail-card__icon">✊</span>
        <h3>Why It Matters</h3>
        <p>We need 15,000 doors knocked by Election Day, November 3, 2026 — and every new voter strengthens our democracy</p>
      </div>
    </div>

    <!-- SIGN UP FORM -->
    <div class="signup-section">
      <h2>Sign Up to Participate</h2>
      <p class="signup-intro">
        Fill out the form below and we'll send you everything you need — event details,
        materials, and how to run a voter registration table in your area.
      </p>

      @if (!submitted) {
        <form #signupForm="ngForm" (ngSubmit)="onSubmit()" class="signup-form">

          <!-- Name + Email -->
          <div class="form-row">
            <div class="form-group">
              <label for="sd-name">Full Name *</label>
              <input
                id="sd-name"
                name="name"
                type="text"
                placeholder="Full Name"
                required
                [(ngModel)]="signup.name"
                #sdName="ngModel"
                [disabled]="isSubmitting"
              />
              @if (sdName.invalid && sdName.touched) {
                <div class="error">Name is required.</div>
              }
            </div>
            <div class="form-group">
              <label for="sd-email">Email Address *</label>
              <input
                id="sd-email"
                name="email"
                type="email"
                placeholder="Email Address"
                required
                pattern="[a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,}$"
                [(ngModel)]="signup.email"
                #sdEmail="ngModel"
                [disabled]="isSubmitting"
              />
              @if (sdEmail.invalid && sdEmail.touched) {
                <div class="error">
                  @if (sdEmail.errors?.['required']) { <span>Email is required.</span> }
                  @if (sdEmail.errors?.['pattern']) { <span>Enter a valid email.</span> }
                </div>
              }
            </div>
          </div>

          <!-- Phone + Zip -->
          <div class="form-row">
            <div class="form-group">
              <label for="sd-phone">Phone Number <span class="optional">(optional)</span></label>
              <input
                id="sd-phone"
                name="phone"
                type="tel"
                placeholder="Phone Number"
                [(ngModel)]="signup.phone"
                [disabled]="isSubmitting"
              />
            </div>
            <div class="form-group">
              <label for="sd-zip">Zip Code *</label>
              <input
                id="sd-zip"
                name="zip"
                type="text"
                placeholder="Zip Code"
                required
                pattern="\d{5}"
                [(ngModel)]="signup.zip"
                #sdZip="ngModel"
                [disabled]="isSubmitting"
              />
              @if (sdZip.invalid && sdZip.touched) {
                <div class="error">
                  @if (sdZip.errors?.['required']) { <span>Zip code is required.</span> }
                  @if (sdZip.errors?.['pattern']) { <span>Must be 5 digits.</span> }
                </div>
              }
            </div>
          </div>

          <!-- County -->
          <div class="form-group">
            <label for="sd-county">Michigan County <span class="optional">(optional)</span></label>
            <select id="sd-county" name="county" [(ngModel)]="signup.county" [disabled]="isSubmitting">
              <option value="">Select Your County</option>
              @for (county of michiganCounties; track county) {
                <option [value]="county">{{ county }}</option>
              }
            </select>
          </div>

          <!-- Registered to vote -->
          <div class="form-group">
            <label for="sd-registered">Are you registered to vote in Michigan? *</label>
            <select
              id="sd-registered"
              name="registered"
              required
              [(ngModel)]="signup.registered"
              #sdRegistered="ngModel"
              [disabled]="isSubmitting"
            >
              <option value="">Select an answer</option>
              <option value="yes">Yes, I'm registered</option>
              <option value="no">No, I need to register</option>
              <option value="unsure">Not sure</option>
            </select>
            @if (sdRegistered.invalid && sdRegistered.touched) {
              <div class="error">Please select an answer.</div>
            }
          </div>

          <!-- Optional message -->
          <div class="form-group">
            <label for="sd-message">Anything you'd like us to know? <span class="optional">(optional)</span></label>
            <textarea
              id="sd-message"
              name="message"
              rows="3"
              placeholder="e.g. I can host a registration table at my workplace, I have questions about the event..."
              [(ngModel)]="signup.message"
              [disabled]="isSubmitting"
            ></textarea>
          </div>

          <button type="submit" class="btn-submit" [disabled]="signupForm.invalid || isSubmitting">
            {{ isSubmitting ? 'Signing Up...' : 'Sign Me Up' }}
          </button>

          @if (errorMessage) {
            <div class="error submit-error">{{ errorMessage }}</div>
          }

        </form>
      }

      <!-- SUCCESS STATE -->
      @if (submitted) {
        <div class="success-message">
          <div class="success-icon">🎉</div>
          <h3>You're signed up!</h3>
          <p>
            Thank you for joining the May Day Voter Registration Drive.
            We'll send you everything you need to make a difference on May 1st.
          </p>
          <div class="success-actions">
            <a routerLink="/volunteer" class="btn-primary">Volunteer for More</a>
            <button type="button" class="btn-secondary" (click)="resetForm()">Sign Up Again</button>
          </div>
        </div>
      }

    </div>

    <!-- ALSO CONSIDER -->
    <div class="also-consider">
      <h2>While You're Here</h2>
      <div class="cta-cards">
        <a routerLink="/volunteer" class="cta-card">
          <span>✍️</span>
          <h3>Knock Doors</h3>
          <p>We need 15,000 doors knocked by Election Day, November 3, 2026 to win Michigan.</p>
        </a>
        <a routerLink="/events" class="cta-card">
          <span>📅</span>
          <h3>See All Events</h3>
          <p>Town halls, canvassing days, and more happening across Michigan.</p>
        </a>
        <a routerLink="/donate" class="cta-card">
          <span>💚</span>
          <h3>Support the Campaign</h3>
          <p>Chip in to help fund voter outreach and grassroots organizing.</p>
        </a>
      </div>
    </div>

  </div>
</section>
